// Service Client - Page pour les ACHETEURS
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  HelpCircle, ArrowLeft, MessageCircle, Phone, Mail, ChevronRight,
  Package, Truck, CreditCard, RotateCcw, Shield, FileText,
  Search, Clock, CheckCircle2, ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

import { API_URL } from '../../config/api';

// Catégories d'aide
const HELP_CATEGORIES = [
  {
    id: 'orders',
    icon: Package,
    label: 'Commandes',
    description: 'Suivi, modification, annulation',
    color: '#3B82F6'
  },
  {
    id: 'delivery',
    icon: Truck,
    label: 'Livraison',
    description: 'Délais, suivi, problèmes',
    color: '#10B981'
  },
  {
    id: 'payment',
    icon: CreditCard,
    label: 'Paiement',
    description: 'Facturation, remboursements',
    color: '#8B5CF6'
  },
  {
    id: 'returns',
    icon: RotateCcw,
    label: 'Retours',
    description: 'Politique de retour, échanges',
    color: '#F59E0B'
  },
  {
    id: 'account',
    icon: Shield,
    label: 'Mon compte',
    description: 'Profil, sécurité, données',
    color: '#EC4899'
  },
  {
    id: 'products',
    icon: FileText,
    label: 'Produits',
    description: 'Garantie, SAV, conseils',
    color: '#0EA5E9'
  },
];

// Questions fréquentes
const FAQ = [
  {
    question: 'Comment suivre ma commande ?',
    answer: 'Rends-toi dans "Mes commandes" depuis ton compte. Tu y trouveras le numéro de suivi et le lien vers le transporteur.'
  },
  {
    question: 'Quels sont les délais de livraison ?',
    answer: 'Les délais varient selon le vendeur : généralement 2-5 jours ouvrés en France métropolitaine. Le délai estimé est indiqué avant la commande.'
  },
  {
    question: 'Comment retourner un produit ?',
    answer: 'Vous disposez de 14 jours après réception pour retourner un produit. Contactez le vendeur via la messagerie pour obtenir l\'étiquette de retour.'
  },
  {
    question: 'Comment contacter un vendeur ?',
    answer: 'Depuis la page du produit ou votre commande, cliquez sur "Contacter le vendeur". Vous pouvez aussi passer par "Mes commandes".'
  },
  {
    question: 'Quels moyens de paiement acceptez-vous ?',
    answer: 'Nous acceptons les cartes bancaires (Visa, Mastercard), PayPal, et les virements bancaires pour les commandes professionnelles.'
  },
];

// Carte catégorie
function CategoryCard({ category, onClick }) {
  const Icon = category.icon;
  return (
    <motion.button
      whileHover={{ y: -2 }}
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:shadow-md transition-all w-full"
    >
      <div 
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
        style={{ backgroundColor: `${category.color}15` }}
      >
        <Icon size={24} style={{ color: category.color }} />
      </div>
      <h3 className="font-semibold text-gray-900">{category.label}</h3>
      <p className="text-sm text-gray-500">{category.description}</p>
    </motion.button>
  );
}

// FAQ Accordion
function FAQItem({ item, isOpen, onToggle }) {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between text-left"
      >
        <span className="font-medium text-gray-900">{item.question}</span>
        <ChevronRight
          size={18}
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`}
        />
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="px-4 pb-4"
        >
          <p className="text-gray-600 text-sm">{item.answer}</p>
        </motion.div>
      )}
    </div>
  );
}

export default function ServiceClientPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);

  const handleContactSupport = () => {
    toast.success('Un conseiller te répondra sous 24h');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="service-client-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Service client</h1>
              <p className="text-white/80">Comment pouvons-nous t'aider ?</p>
            </div>
          </div>

          {/* Barre de recherche */}
          <div className="relative">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher dans l'aide..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-3 rounded-xl bg-white text-gray-900 border-0"
            />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Catégories */}
        <div className="mb-8">
          <h2 className="font-bold text-gray-900 mb-4">Catégories d'aide</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HELP_CATEGORIES.map((cat) => (
              <CategoryCard
                key={cat.id}
                category={cat}
                onClick={() => toast.info(`Section "${cat.label}" à venir`)}
              />
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-8">
          <h2 className="font-bold text-gray-900 mb-4">Questions fréquentes</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {FAQ.map((item, idx) => (
              <FAQItem
                key={idx}
                item={item}
                isOpen={openFAQ === idx}
                onToggle={() => setOpenFAQ(openFAQ === idx ? null : idx)}
              />
            ))}
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="font-bold text-gray-900 mb-4">Besoin d'aide supplémentaire ?</h2>
          
          <div className="grid sm:grid-cols-3 gap-4 mb-6">
            <button
              onClick={handleContactSupport}
              className="p-4 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors text-center"
            >
              <MessageCircle size={24} className="text-blue-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Chat</p>
              <p className="text-xs text-gray-500">Réponse sous 24h</p>
            </button>
            <a
              href="mailto:support@abracadabati.fr"
              className="p-4 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-colors text-center"
            >
              <Mail size={24} className="text-green-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Email</p>
              <p className="text-xs text-gray-500">support@abracadabati.fr</p>
            </a>
            <a
              href="tel:0123456789"
              className="p-4 border border-gray-200 rounded-xl hover:border-orange-300 hover:bg-orange-50 transition-colors text-center"
            >
              <Phone size={24} className="text-orange-600 mx-auto mb-2" />
              <p className="font-medium text-gray-900">Téléphone</p>
              <p className="text-xs text-gray-500">01 23 45 67 89</p>
            </a>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 flex items-center gap-3">
            <Clock size={20} className="text-gray-500" />
            <div>
              <p className="text-sm font-medium text-gray-900">Horaires du service client</p>
              <p className="text-xs text-gray-500">Lun-Ven : 9h-18h • Sam : 9h-12h</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
